/* Compact Mass Messages — the data model shared by Mass Messages and Priority
   Mass Messages. Both products have the same lists, campaigns and schedule;
   they differ in a few campaign flags (message source, keep in DMs) and in how
   a scheduled row is taken back (delete vs. cancel). Pure functions only, so
   the pages stay thin and this file can be unit-tested. */

import type { MassMessageCampaign, MassMessageSchedule, PriorityMassMessageCampaign, PriorityMassMessageSchedule } from '../../../api/client';
import { decodeFanslyAudiencePicker, encodeFanslyAudience, fanslyAudiencePickerIds, hasFanslyAudience } from '../fanslyAudience';
import { priorityAudienceFromPicker, priorityAudienceToPicker } from '../../PriorityMassMessages/priorityFanslyAudience';

export type Kind = 'mass' | 'priority';
export type Platform = 'onlyfans' | 'fansly';

/** Purple-only list palette (design: P). A list keeps its colour by position. */
export const LIST_COLORS = ['var(--om-accent)', 'var(--om-accent-2)', 'var(--om-accent-deep)', 'var(--om-accent-3)', 'var(--om-accent-hover)', '#6a4fd6', 'var(--om-accent-4)', 'var(--om-accent-2)'];
/** Light swatches need a dark icon on top. */
export const LIGHT_COLORS = new Set(['var(--om-accent-2)', 'var(--om-accent-3)', 'var(--om-accent-4)', 'var(--om-accent-2)']);
export const DELETED_COLOR = 'var(--om-border-strong)';

export const listColor = (index: number) => LIST_COLORS[((index % LIST_COLORS.length) + LIST_COLORS.length) % LIST_COLORS.length]!;

/* ── Campaigns ─────────────────────────────────────────────────────── */

export interface DistEntry { id: string; w: number } // w in whole percent

/** One campaign, in the shape the pages and the editor work with. */
export interface Camp {
  id: number | null;
  name: string;
  active: boolean;
  source: 'custom' | 'auto';
  dist: DistEntry[];
  inc: string[];
  exc: string[];
  /** Visible for (delete after), minutes. */
  vis: [number, number];
  /** Gap between messages (send cadence), minutes. */
  gap: [number, number];
  /** Priority only: never delete sent messages (ADR 0001). */
  keep: boolean;
  sends: number;
}

type RawCamp = MassMessageCampaign | PriorityMassMessageCampaign;

/** Fansly mass campaigns keep their audience in the fansly_* fields; everything
 *  else (OnlyFans, and Priority on both platforms) uses user_lists_*. */
const fanslyAudience = (kind: Kind, platform: Platform) => kind === 'mass' && platform === 'fansly';

export function campOf(c: RawCamp, kind: Kind, platform: Platform): Camp {
  const m = c as MassMessageCampaign;
  const fa = fanslyAudience(kind, platform);
  return {
    id: c.id,
    name: c.name,
    active: c.is_active,
    source: kind === 'mass' ? (m.message_source || 'custom') : 'custom',
    dist: Object.entries(c.lists_distribution || {}).map(([id, w]) => ({ id, w: Math.round(Number(w) * 100) })),
    inc: fa ? fanslyAudiencePickerIds(m.fansly_include_list_ids || []) : kind === 'priority' && platform === 'fansly'
      ? priorityAudienceToPicker(c.user_lists_to_include || []) : [...(c.user_lists_to_include || [])],
    exc: [...((fa ? m.fansly_exclude_list_ids : c.user_lists_to_exclude) || [])],
    vis: [c.delete_after_min_minutes ?? 40, c.delete_after_max_minutes ?? 50],
    gap: [c.send_cadence_min_minutes ?? 1, c.send_cadence_max_minutes ?? 3],
    keep: kind === 'priority' ? !!(c as PriorityMassMessageCampaign).skip_deletion : false,
    sends: c.total_sends ?? 0,
  };
}

/** A new campaign. Priority defaults to a 2–3 min gap, Mass to 1–3. */
export function blankCamp(kind: Kind): Camp {
  return { id: null, name: '', active: true, source: 'custom', dist: [], inc: [], exc: [], vis: [40, 50], gap: kind === 'priority' ? [2, 3] : [1, 3], keep: false, sends: 0 };
}

/** Request body for create/update. Min priority level is no longer edited, so
 *  it isn't sent and the stored value (or the server default) stays. */
export function payloadOf(c: Camp, kind: Kind, platform: Platform) {
  const auto = kind === 'mass' && c.source === 'auto';
  const body: Record<string, unknown> = {
    name: c.name.trim(),
    is_active: c.active,
    lists_distribution: auto ? {} : Object.fromEntries(c.dist.map((d) => [d.id, d.w / 100])),
    send_cadence_min_minutes: c.gap[0],
    send_cadence_max_minutes: c.gap[1],
    delete_after_min_minutes: c.vis[0],
    delete_after_max_minutes: c.vis[1],
  };
  if (kind === 'mass') body.message_source = c.source;
  if (kind === 'priority') body.skip_deletion = c.keep;
  if (fanslyAudience(kind, platform)) {
    const audience = decodeFanslyAudiencePicker(c.inc);
    Object.assign(body, { user_lists_to_include: [], user_lists_to_exclude: [], fansly_group_ids: [], fansly_include_list_ids: encodeFanslyAudience(audience.flags, audience.listIds), fansly_exclude_list_ids: c.exc });
  } else if (kind === 'priority' && platform === 'fansly') {
    Object.assign(body, { user_lists_to_include: priorityAudienceFromPicker(c.inc), user_lists_to_exclude: c.exc });
  } else {
    Object.assign(body, { user_lists_to_include: c.inc, user_lists_to_exclude: c.exc });
  }
  return body;
}

export const distTotal = (dist: DistEntry[]) => dist.reduce((a, d) => a + d.w, 0);

/** Equal weights that add up to exactly 100 (the first lists take the remainder). */
export function splitEvenly(dist: DistEntry[]): DistEntry[] {
  const n = dist.length;
  if (!n) return dist;
  const b = Math.floor(100 / n);
  return dist.map((d, j) => ({ ...d, w: b + (j < 100 - b * n ? 1 : 0) }));
}

export type Issue =
  | { code: 'name' }
  | { code: 'lists' }
  | { code: 'weights'; total: number }
  | { code: 'audience' };

/** What still blocks saving, in the order the footer shows them. `listIds` are
 *  the lists that still exist — a weight on a deleted list doesn't count. */
export function campIssues(c: Camp, kind: Kind, listIds: Set<string>, platform: Platform = 'onlyfans'): Issue[] {
  const out: Issue[] = [];
  if (!c.name.trim()) out.push({ code: 'name' });
  const usesLists = !(kind === 'mass' && c.source === 'auto');
  if (usesLists) {
    if (!c.dist.some((d) => listIds.has(d.id))) out.push({ code: 'lists' });
    else if (distTotal(c.dist) !== 100) out.push({ code: 'weights', total: distTotal(c.dist) });
  }
  if (fanslyAudience(kind, platform)) {
    try {
      const audience = decodeFanslyAudiencePicker(c.inc);
      if (!hasFanslyAudience(audience.flags, audience.listIds)) out.push({ code: 'audience' });
    } catch { out.push({ code: 'audience' }); }
  } else if (kind === 'priority' && platform === 'fansly') {
    try { priorityAudienceFromPicker(c.inc); } catch { out.push({ code: 'audience' }); }
  } else if (!c.inc.length) out.push({ code: 'audience' });
  return out;
}

/** Step one end of a [from, to] range, dragging the other end along so from ≤ to. */
export function stepRange(r: [number, number], end: 0 | 1, delta: number, lo: number, hi: number): [number, number] {
  const next: [number, number] = [r[0], r[1]];
  next[end] = Math.max(lo, Math.min(hi, next[end] + delta));
  if (next[0] > next[1]) next[end ? 0 : 1] = next[end];
  return next;
}

/* ── Schedule ──────────────────────────────────────────────────────── */

export type Row = MassMessageSchedule | PriorityMassMessageSchedule;
export type StatusFilter = 'all' | 'pending' | 'sent' | 'unsent' | 'failed';
/** What a row shows. 'removing' is a priority cancel still in flight. */
export type RowStatus = 'pending' | 'sent' | 'unsent' | 'failed' | 'removing';

const cancelState = (r: Row) => (r as PriorityMassMessageSchedule).cancel_state ?? null;
const cancelling = (r: Row) => cancelState(r) === 'requested' || cancelState(r) === 'processing';

export function rowStatus(r: Row): RowStatus {
  if (cancelling(r)) return 'removing';
  switch (r.status) {
    case 'pending': case 'sending': return 'pending';
    case 'sent': case 'kept': return 'sent'; // kept: keep mode, left in fans' DMs
    case 'deleted': case 'deleting': case 'cancelled': return 'unsent';
    case 'failed': return 'failed';
    default: return 'pending';
  }
}

export function matchesFilter(r: Row, f: StatusFilter): boolean {
  if (f === 'all') return true;
  const s = rowStatus(r);
  return s === f || (f === 'unsent' && s === 'removing');
}

/** Mass: a row can be edited or deleted until it has gone out. */
export const massCanRemove = (r: Row) => !['sent', 'deleted', 'failed', 'cancelled'].includes(r.status);

/** Priority: a row is taken back by cancelling it — before it's sent, or (once
 *  sent) by removing it from fans' chats. Nothing to do once it's gone. */
export function priorityCanRemove(r: Row): boolean {
  const p = r as PriorityMassMessageSchedule;
  if (cancelling(r)) return false;
  if (['pending', 'sending', 'sent', 'deleting'].includes(r.status)) return true;
  return r.status === 'failed' && (p.successful_sends ?? 0) > (p.successful_deletes ?? 0);
}

export const canRemove = (kind: Kind, r: Row) => (kind === 'priority' ? priorityCanRemove(r) : massCanRemove(r));
export const canEdit = (kind: Kind, r: Row) => (kind === 'priority' ? r.status === 'pending' && !cancelState(r) : massCanRemove(r));
export const isQueued = (r: Row) => r.status === 'pending' || r.status === 'sending';

/** content_id holds one vault item today; split defensively in case it ever carries several. */
export const contentIds = (r: Row): string[] => (r.content_id ? String(r.content_id).split(',').map((s) => s.trim()).filter(Boolean) : []);

/** Campaign id → queued (pending) count. */
export function queuedByCampaign(rows: Row[]): Map<number, number> {
  const m = new Map<number, number>();
  rows.forEach((r) => { if (isQueued(r)) m.set(r.campaign_id, (m.get(r.campaign_id) ?? 0) + 1); });
  return m;
}
